import { Button, Card, CardBody, CardHeader, Skeleton } from '@heroui/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Database, Layers, Zap } from 'lucide-react';

import { useDatasets } from '@/lib/api/hooks/datasets';
import { useModels } from '@/lib/api/hooks/models';
import { useTrainingJobs } from '@/lib/api/hooks/training';
import { useInferenceJobs } from '@/lib/api/hooks/inference';
import { useUsage } from '@/lib/api/hooks/billing';
import { formatRelativeTime } from '@/lib/design-system';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

function StatItem({
  label,
  value,
  isLoading,
}: {
  label: string;
  value: number | string;
  isLoading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-default-500">{label}</span>
      {isLoading ? (
        <Skeleton className="h-7 w-16 rounded" />
      ) : (
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
      )}
    </div>
  );
}

function DashboardPage() {
  const { data: datasets, isLoading: isLoadingDatasets } = useDatasets();
  const { data: models, isLoading: isLoadingModels } = useModels();
  const { data: trainingJobs, isLoading: isLoadingTraining } = useTrainingJobs();
  const { data: inferenceJobs, isLoading: isLoadingInference } = useInferenceJobs();
  const { data: usage, isLoading: isLoadingUsage } = useUsage();

  // Calculate total audio files from datasets
  const totalAudioFiles = datasets?.reduce((acc, d) => acc + (d.audio_count ?? 0), 0) ?? 0;

  // Get recent activity (combine all recent items)
  const recentActivity = [
    ...(trainingJobs?.slice(0, 3).map((job) => ({
      type: 'training' as const,
      title: `Training ${job.status}`,
      date: new Date(job.created_at),
      status: job.status,
    })) ?? []),
    ...(inferenceJobs?.slice(0, 3).map((job) => ({
      type: 'inference' as const,
      title: `Inference ${job.status}: ${job.total_files ?? 0} files`,
      date: new Date(job.created_at),
      status: job.status,
    })) ?? []),
    ...(datasets?.slice(0, 3).map((dataset) => ({
      type: 'dataset' as const,
      title: `Dataset created: ${dataset.name}`,
      date: new Date(dataset.created_at),
      status: 'created',
    })) ?? []),
  ]
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 5);

  const hasData = (datasets?.length ?? 0) > 0 || (models?.length ?? 0) > 0;

  return (
    <div className="container-page py-4 animate-fade-in">
      {/* Stats */}
      <div className="flex items-start gap-8 mb-6 py-4 border-b border-default-100">
        <StatItem label="Datasets" value={datasets?.length ?? 0} isLoading={isLoadingDatasets} />
        <div className="h-10 w-px bg-default-100" />
        <StatItem label="Audio Files" value={totalAudioFiles} isLoading={isLoadingDatasets} />
        <div className="h-10 w-px bg-default-100" />
        <StatItem label="Models" value={models?.length ?? 0} isLoading={isLoadingModels} />
        <div className="h-10 w-px bg-default-100" />
        <StatItem label="Inference Min" value={usage?.inference_minutes ?? 0} isLoading={isLoadingUsage} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card className="border border-default-100 shadow-none animate-slide-up">
          <CardHeader className="pb-1 pt-3 px-4">
            <h2 className="text-sm font-medium">Quick Actions</h2>
          </CardHeader>
          <CardBody className="space-y-2 px-4 pb-4 pt-2">
            <Button
              as={Link}
              to="/datasets/new"
              variant="flat"
              size="sm"
              className="w-full justify-start"
            >
              Create Dataset
            </Button>
            <Button
              as={Link}
              to="/training/new"
              variant="flat"
              size="sm"
              className="w-full justify-start"
              isDisabled={(datasets?.length ?? 0) === 0}
            >
              Train Model
            </Button>
            <Button
              as={Link}
              to="/inference/new"
              variant="flat"
              size="sm"
              className="w-full justify-start"
              isDisabled={(models?.length ?? 0) === 0}
            >
              Run Inference
            </Button>
          </CardBody>
        </Card>

        {/* Recent Activity */}
        <Card className="border border-default-100 shadow-none animate-slide-up">
          <CardHeader className="pb-1 pt-3 px-4">
            <h2 className="text-sm font-medium">Recent Activity</h2>
          </CardHeader>
          <CardBody className="px-4 pb-4 pt-2">
            {(isLoadingTraining || isLoadingInference || isLoadingDatasets) && (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 rounded" />
                ))}
              </div>
            )}
            {!isLoadingTraining && !isLoadingInference && !isLoadingDatasets && (
              <>
                {recentActivity.length === 0 ? (
                  <p className="text-center text-xs text-default-500 py-3">
                    No recent activity. Create a dataset to get started.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {recentActivity.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-md border border-default-100 px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          {item.type === 'training' && (
                            <Layers className="h-3.5 w-3.5 text-primary" />
                          )}
                          {item.type === 'inference' && (
                            <Zap className="h-3.5 w-3.5 text-warning" />
                          )}
                          {item.type === 'dataset' && (
                            <Database className="h-3.5 w-3.5 text-secondary" />
                          )}
                          <span className="text-xs">{item.title}</span>
                        </div>
                        <span className="text-xs text-default-400">
                          {formatRelativeTime(item.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Getting Started (shown when no data) */}
      {!hasData && !isLoadingDatasets && !isLoadingModels && (
        <div className="mt-6 border-t border-default-100 pt-6">
          <h2>Getting Started</h2>
          <ol className="mt-3 list-inside list-decimal space-y-1.5 text-sm text-default-600">
            <li>Create a dataset and define artifact types</li>
            <li>Upload audio files with labeled samples</li>
            <li>Add annotations marking artifact locations</li>
            <li>Train a custom detection model</li>
            <li>Run inference on new audio files</li>
          </ol>
        </div>
      )}
    </div>
  );
}
